import { useEffect, useMemo, useState } from 'react'

import { DatePickerOpenRangePayload } from '../DatePicker.types'
import { formatDateToString, parseStringToDate } from '../DatePicker.utils'
import {
  UseDatePickerPayload,
  UseDatePickerValueProps,
  UseDatePickerValueReturn,
} from './useDatePickerValue.types'

export function useDatePickerValue<AllowOpenRange extends boolean = false>({
  initialValue,
  allowOpenRange,
  onChange,
}: UseDatePickerValueProps<AllowOpenRange>) {
  const [rawValue, setRawValue] = useState(() => initialValue)
  const [metadata, setMetadata] = useState<DatePickerOpenRangePayload['metadata']>()

  const formattedValue = useMemo(() => {
    if (!rawValue) return undefined

    const [startDate, endDate] = rawValue.split('_')
    const parsedStartDate = startDate ? parseStringToDate(startDate) : undefined
    const parsedEndDate = endDate ? parseStringToDate(endDate) : undefined

    return { startDate: parsedStartDate, endDate: parsedEndDate }
  }, [rawValue])

  const handleChange = (payload?: UseDatePickerPayload<AllowOpenRange>) => {
    if (!payload) return handleClear()

    const startDate = payload.value.startDate ? formatDateToString(payload.value.startDate) : ''
    const endDate = payload.value.endDate ? formatDateToString(payload.value.endDate) : ''

    if (!allowOpenRange && (!startDate || !endDate)) return handleClear()

    const newValue = `${startDate}_${endDate}`

    setRawValue(newValue)
    setMetadata(payload.metadata)
    onChange(newValue)
  }

  const handleClear = () => {
    setRawValue(undefined)
    setMetadata(undefined)
    onChange(undefined)
  }

  useEffect(() => {
    setRawValue(prev => (prev !== initialValue ? initialValue : prev))
  }, [initialValue])

  return {
    value: formattedValue,
    metadata,
    handleChange,
    handleClear,
  } as UseDatePickerValueReturn<AllowOpenRange>
}
